import React, { useEffect, useMemo, useRef, useState } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Image, message, Modal, Pagination, Space, Spin } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import CommonTable from '../../commonComponents/Table/table';
import { getPresignedUrlForPIIUpload, useAddOtherImages, useRetagArticle } from '../../hooks/useOrders';
import CSATagDataModalContent from './CSATagDataModalContent';

const CSAFilteredProductsTable = ({ data = [], loading = false, total = 0, pageNum = 1, pageSize = 10, onPageChange, onRefetch, companyCode }) => {
    const [infoModalOpen, setInfoModalOpen] = useState(false);
    const [csvModalOpen, setCsvModalOpen] = useState(false);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [tagDataModalOpen, setTagDataModalOpen] = useState(false);
    const [tagDataPairs, setTagDataPairs] = useState([{ key: '', value: '' }]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (selectedProduct && data?.length > 0) {
            const updated = data.find(r => r._id === selectedProduct._id);
            if (updated) setSelectedProduct(updated);
        }
    }, [data]);

    const { mutate: addOtherImages } = useAddOtherImages(
        () => {
            setUploading(false);
            message.success('Images uploaded successfully');
            if (onRefetch) onRefetch();
        },
        error => {
            message.error(error?.message ?? 'Upload failed');
            setUploading(false);
        }
    );

    const handleDirectUpload = async e => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        setUploading(true);
        try {
            const keys = await Promise.all(
                files.map(async file => {
                    const resp = await getPresignedUrlForPIIUpload({ filename: file.name, mimeType: file.type, osbId: selectedProduct?.osbid });
                    const { url, key } = resp.data;
                    await axios.put(url, file, { headers: { 'Content-Type': file.type } });
                    return { key };
                })
            );
            addOtherImages({ csaId: selectedProduct._id, otherImages: keys });
        } catch (err) {
            message.error(err?.message ?? 'Upload failed');
            setUploading(false);
        }
    };

    const { mutate: retagArticle } = useRetagArticle(
        () => {
            setTagDataModalOpen(false);
            setTagDataPairs([{ key: '', value: '' }]);
            message.success('Tag data added successfully');
            if (onRefetch) onRefetch();
        },
        error => {
            message.error(error.message);
        }
    );

    const infoRows = useMemo(
        () => [
            {
                key: 'articleSku',
                label: 'Article / SKU',
                value: selectedProduct?.skuName ?? selectedProduct?.articleName ?? '—',
            },
            {
                key: 'orderName',
                label: 'Order Name',
                value: selectedProduct?.orderName ?? '—',
            },
            {
                key: 'brand',
                label: 'Brand',
                value: selectedProduct?.csvData?.brand_name ?? selectedProduct?.csvData?.brand ?? '—',
            },
            {
                key: 'configSku',
                label: 'Config SKU',
                value: selectedProduct?.csvData?.Config_SKU ?? selectedProduct?.csvData?.config_sku ?? '—',
            },
            {
                key: 'otherImages',
                label: 'Other Images',
                value: uploading ? (
                    <Spin size="small" tip="Uploading..." />
                ) : Array.isArray(selectedProduct?.otherImages) && selectedProduct.otherImages.length > 0 ? (
                    <Image.PreviewGroup items={selectedProduct.otherImages.map(img => ({ src: img?.thumbPath?.url }))}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {selectedProduct.otherImages.map(img => (
                                <Image
                                    key={img._id}
                                    src={img?.thumbPath?.url}
                                    preview={{ src: img?.thumbPath?.url }}
                                    width={64}
                                    height={64}
                                    style={{ objectFit: 'cover', borderRadius: 4 }}
                                    alt={img?.name}
                                />
                            ))}
                        </div>
                    </Image.PreviewGroup>
                ) : (
                    '—'
                ),
            },
        ],
        [selectedProduct, uploading]
    );

    const csvDataColumns = useMemo(
        () => [
            {
                title: 'Field',
                dataIndex: 'field',
                key: 'field',
                width: 220,
            },
            {
                title: 'Value',
                dataIndex: 'value',
                key: 'value',
                ellipsis: true,
            },
        ],
        []
    );

    const csvDataRows = useMemo(() => {
        const csvData = selectedProduct?.csvData;
        if (!csvData || typeof csvData !== 'object') return [];
        return Object.entries(csvData).map(([field, value]) => ({
            key: field,
            field,
            value: value ?? '—',
        }));
    }, [selectedProduct]);

    const historyColumns = useMemo(
        () => [
            {
                title: 'Time',
                dataIndex: 'timestamp',
                key: 'timestamp',
                render: (val, record) => {
                    const ts = val || record?.createdAt || record?.updatedAt;
                    return ts ? dayjs(ts).format('YYYY-MM-DD HH:mm:ss') : '—';
                },
                width: 180,
            },
            {
                title: 'Action',
                dataIndex: 'action',
                key: 'action',
                render: val => val ?? '—',
            },
            {
                title: 'From → To',
                key: 'fromTo',
                render: (_, record) => `${record?.fromLocation ?? '—'} → ${record?.toLocation ?? '—'}`,
            },
            {
                title: 'Issued By',
                key: 'issuedBy',
                render: (_, record) => record?.issuedBy?.name ?? record?.issuedBy?.email ?? '—',
            },
            {
                title: 'Issued To',
                key: 'issuedTo',
                render: (_, record) => record?.issuedTo?.name ?? record?.issuedTo?.email ?? '—',
            },
        ],
        []
    );

    const showGrid = !loading && data?.length > 0;
    const showEmpty = !loading && (!data || data.length === 0);
    const showPagination = !loading && (data?.length > 0 || total > 0);

    return (
        <div className="csa-filtered-products">
            <div className="csa-filtered-products-body">
                {loading && data.length === 0 && (
                    <div className="csa-list-spin-center">
                        <Spin size="large" />
                    </div>
                )}

                {showEmpty && <div className="csa-list-empty">No products found</div>}

                {showGrid && (
                    <div className="csa-list-table-wrap csa-product-grid-wrap">
                        <div className="csa-product-grid">
                            {data.map(record => {
                                const productKey =
                                    record?._id ??
                                    record?.id ??
                                    record?.tagData?.barcode ??
                                    record?.barcodes?.[0] ??
                                    record?.csvData?.barcode ??
                                    `${record?.articleName ?? 'item'}-${record?.skuName ?? ''}`;

                                const imageUrl = record?.articleImage?.thumbPath?.url;
                                const previewSrc = record?.articleImage?.imagePath?.url;
                                const label =
                                    record?.csvData?.barcode ??
                                    record?.tagData?.barcode ??
                                    record?.barcodes?.[0] ??
                                    record?.skuName ??
                                    record?.articleName ??
                                    '—';

                                return (
                                    <div className="csa-product-card" key={productKey}>
                                        <Button
                                            type="text"
                                            className="csa-product-info-btn"
                                            icon={<InfoCircleOutlined />}
                                            onClick={() => {
                                                setSelectedProduct(record);
                                                setInfoModalOpen(true);
                                            }}
                                        />

                                        <div className="csa-product-image-box">
                                            {imageUrl ? (
                                                <Image
                                                    src={imageUrl}
                                                    alt={record?.skuName ?? record?.articleName ?? 'product-image'}
                                                    className="csa-product-image"
                                                    preview={previewSrc ? { src: previewSrc } : true}
                                                />
                                            ) : (
                                                <div className="csa-product-image-placeholder">No image</div>
                                            )}
                                        </div>

                                        <div className="csa-product-label">{label}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            <div className="csa-list-footer">
                {showPagination && onPageChange && (
                    <div className="csa-list-pagination">
                        <Pagination
                            current={pageNum}
                            pageSize={pageSize}
                            total={total}
                            showSizeChanger
                            onChange={(page, size) => {
                                onPageChange(page, size || pageSize);
                            }}
                        />
                    </div>
                )}
            </div>

            {selectedProduct && (
                <Modal
                    title={`Product Info - ${selectedProduct?.skuName ?? selectedProduct?.articleName ?? 'Product'}`}
                    open={infoModalOpen}
                    onCancel={() => {
                        setInfoModalOpen(false);
                    }}
                    footer={null}
                    destroyOnClose
                    width="70%"
                    bodyStyle={{ maxHeight: '65vh', overflowY: 'auto' }}
                >
                    {infoRows.length > 0 ? (
                        <div className="csa-product-info-list">
                            {infoRows.map(item => (
                                <div className="csa-product-info-row" key={item.key}>
                                    <div className="csa-product-info-key">{item.label}</div>
                                    <div className="csa-product-info-value">{item.value}</div>
                                </div>
                            ))}
                            <div className="csa-product-info-row" key="actionLinks">
                                <div className="csa-product-info-key">Action Links</div>
                                <div className="csa-product-info-value">
                                    <Button
                                        type="link"
                                        onClick={() => {
                                            setCsvModalOpen(true);
                                        }}
                                    >
                                        View CSV Data
                                    </Button>
                                    <Button
                                        type="link"
                                        onClick={() => {
                                            setHistoryModalOpen(true);
                                        }}
                                    >
                                        View History
                                    </Button>
                                    <Button
                                        type="link"
                                        onClick={() => {
                                            setTagDataModalOpen(true);
                                        }}
                                    >
                                        Add tag data
                                    </Button>
                                    <Button type="link" onClick={() => fileInputRef.current?.click()}>
                                        Upload Images
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="csa-history-empty">No product details available.</div>
                    )}
                </Modal>
            )}

            {selectedProduct && (
                <Modal
                    title={`CSV Data - ${selectedProduct?.skuName ?? selectedProduct?.articleName ?? 'Product'}`}
                    open={csvModalOpen}
                    onCancel={() => {
                        setCsvModalOpen(false);
                    }}
                    footer={null}
                    destroyOnClose
                    width="70%"
                    bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
                >
                    {csvDataRows.length > 0 ? (
                        <CommonTable
                            columns={csvDataColumns}
                            data={csvDataRows}
                            rowKey={record => record.key}
                            pagination={false}
                            bordered
                            size="small"
                        />
                    ) : (
                        <div className="csa-history-empty">No CSV data available.</div>
                    )}
                </Modal>
            )}

            {selectedProduct && (
                <Modal
                    title={`Movement History - ${selectedProduct?.skuName ?? selectedProduct?.articleName ?? 'Product'}`}
                    open={historyModalOpen}
                    onCancel={() => {
                        setHistoryModalOpen(false);
                    }}
                    footer={null}
                    destroyOnClose
                    width="80%"
                    bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
                >
                    {Array.isArray(selectedProduct?.movementHistory) && selectedProduct.movementHistory.length > 0 ? (
                        <CommonTable
                            columns={historyColumns}
                            data={selectedProduct.movementHistory}
                            rowKey={record => record?._id ?? record?.id ?? record?.timestamp ?? record?.createdAt ?? record?.updatedAt}
                            pagination={false}
                            bordered
                            size="small"
                        />
                    ) : (
                        <div className="csa-history-empty">No movement history available.</div>
                    )}
                </Modal>
            )}
            {selectedProduct && (
                <Modal
                    title={`Add Tag Data - ${selectedProduct?.skuName ?? selectedProduct?.articleName ?? 'Product'}`}
                    open={tagDataModalOpen}
                    onCancel={() => {
                        setTagDataModalOpen(false);
                        setTagDataPairs([{ key: '', value: '' }]);
                    }}
                    okText="Add"
                    cancelText="Cancel"
                    onOk={() => {
                        const articleId = selectedProduct?.tagDataImageId;
                        const barcode = selectedProduct?.csvData?.EAN;
                        const existingTagData = selectedProduct?.tagData ?? {};
                        const newPairs = tagDataPairs.reduce((acc, { key, value }) => {
                            if (key) acc[key] = value;
                            return acc;
                        }, {});
                        retagArticle({
                            articleId,
                            tagData: {
                                generatedData: {
                                    barcode,
                                    ...existingTagData,
                                    ...newPairs,
                                },
                            },
                        });
                        setTagDataModalOpen(false);
                        setTagDataPairs([{ key: '', value: '' }]);
                    }}
                    destroyOnClose
                    width="70%"
                    bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
                >
                    <CSATagDataModalContent
                        selectedProduct={selectedProduct}
                        tagDataPairs={tagDataPairs}
                        setTagDataPairs={setTagDataPairs}
                        companyCode={selectedProduct?.companyCode}
                        onRemoveExistingTag={key => {
                            setSelectedProduct(prev => {
                                const updatedTagData = { ...prev.tagData };
                                delete updatedTagData[key];
                                return { ...prev, tagData: updatedTagData };
                            });
                        }}
                    />
                </Modal>
            )}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleDirectUpload}
                onClick={e => {
                    e.target.value = null;
                }}
            />
        </div>
    );
};

export default CSAFilteredProductsTable;
